import * as React from "react";
import { Minus, Plus } from "lucide-react";

import { cn } from "../../lib/utils";

export interface NumberInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onChange"
> {
  value?: number;
  onChange?: (value: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  showControls?: boolean;
  allowDecimal?: boolean;
  decimalPlaces?: number;
  prefix?: string;
  suffix?: string;
}

const stepperClass = cn(
  "inline-flex h-full w-7 shrink-0 items-center justify-center",
  "text-text-tertiary",
  "transition-[background-color,color] duration-100 ease-out-fast",
  "hover:bg-hover hover:text-text active:bg-hover-strong",
  "focus-visible:outline-none focus-fill",
  "disabled:pointer-events-none disabled:opacity-50",
);

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      className,
      value,
      onChange,
      min,
      max,
      step = 1,
      showControls = true,
      allowDecimal = true,
      decimalPlaces = 2,
      prefix,
      suffix,
      disabled,
      ...props
    },
    ref,
  ) => {
    const [displayValue, setDisplayValue] = React.useState(
      value?.toString() ?? "",
    );

    React.useEffect(() => {
      setDisplayValue(value?.toString() ?? "");
    }, [value]);

    const parseValue = (str: string): number | undefined => {
      if (!str) return undefined;
      const num = allowDecimal ? parseFloat(str) : parseInt(str, 10);
      if (isNaN(num)) return undefined;
      return num;
    };

    const clampValue = (num: number): number => {
      let result = num;
      if (min !== undefined) result = Math.max(result, min);
      if (max !== undefined) result = Math.min(result, max);
      if (!allowDecimal) result = Math.round(result);
      else if (decimalPlaces !== undefined) {
        result = parseFloat(result.toFixed(decimalPlaces));
      }
      return result;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const validPattern = allowDecimal ? /^-?\d*\.?\d*$/ : /^-?\d*$/;
      if (!validPattern.test(inputValue) && inputValue !== "") return;

      setDisplayValue(inputValue);

      const parsed = parseValue(inputValue);
      if (parsed !== undefined) {
        onChange?.(clampValue(parsed));
      } else if (inputValue === "" || inputValue === "-") {
        onChange?.(undefined);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const parsed = parseValue(displayValue);
      if (parsed !== undefined) {
        const clamped = clampValue(parsed);
        setDisplayValue(clamped.toString());
        onChange?.(clamped);
      } else {
        setDisplayValue("");
        onChange?.(undefined);
      }
      props.onBlur?.(e);
    };

    const increment = () => {
      const current = value ?? min ?? 0;
      const newValue = clampValue(current + step);
      setDisplayValue(newValue.toString());
      onChange?.(newValue);
    };

    const decrement = () => {
      const current = value ?? max ?? 0;
      const newValue = clampValue(current - step);
      setDisplayValue(newValue.toString());
      onChange?.(newValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        increment();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        decrement();
      }
      props.onKeyDown?.(e);
    };

    return (
      <div
        className={cn(
          // One filled well for the whole control, matching `Input` (§1); the
          // steppers separate from the number by their own hover wash, which is
          // what the internal hairlines used to do.
          "inline-flex h-control-lg items-center overflow-hidden rounded-md",
          // `bg-field`, the token `Input` takes — the well and the bare
          // `<input>` inside it have to land on one colour, and an app that
          // re-points the control fill moves only that token.
          "bg-field",
          "transition-[background-color] duration-100 ease-out-fast",
          "hover:bg-field-strong",
          // The whole well takes the focus fill; see `.focus-fill-within`.
          "focus-fill-within",
          disabled && "opacity-50",
          className,
        )}
      >
        {showControls && (
          <button
            type="button"
            className={stepperClass}
            onClick={decrement}
            disabled={disabled || (min !== undefined && (value ?? 0) <= min)}
            tabIndex={-1}
          >
            <Minus className="size-3.5" aria-hidden />
            <span className="sr-only">Decrease</span>
          </button>
        )}
        <div className="relative min-w-15 flex-1">
          {prefix && (
            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-text-tertiary">
              {prefix}
            </span>
          )}
          <input
            ref={ref}
            /*
              The base layer paints every bare `input` with `--color-field`, at a
              specificity the `bg-transparent` utility cannot reach. Here that
              filled a lighter rectangle between the two steppers and banded the
              control into three blocks. `data-bare-input` is the opt-out that
              rule ships for controls which draw their own well — the wrapper is
              the well.
            */
            data-bare-input=""
            type="text"
            inputMode={allowDecimal ? "decimal" : "numeric"}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={cn(
              "h-control-lg w-full bg-transparent px-2 text-center text-sm text-text tabular-nums",
              "placeholder:text-text-tertiary",
              // No `focus-fill` here — the wrapper carries it, so the steppers
              // light with the number instead of staying dark beside it.
              "focus:outline-none disabled:cursor-not-allowed",
              prefix && "pl-5",
              suffix && "pr-4",
            )}
            {...props}
          />
          {suffix && (
            <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-text-tertiary">
              {suffix}
            </span>
          )}
        </div>
        {showControls && (
          <button
            type="button"
            className={stepperClass}
            onClick={increment}
            disabled={disabled || (max !== undefined && (value ?? 0) >= max)}
            tabIndex={-1}
          >
            <Plus className="size-3.5" aria-hidden />
            <span className="sr-only">Increase</span>
          </button>
        )}
      </div>
    );
  },
);
NumberInput.displayName = "NumberInput";

export { NumberInput };
